import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import {
  websiteSourceService,
  userService,
  brandService,
} from '../../services/api';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import {
  ClipboardDocumentIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CodeBracketIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// ── Copy button ───────────────────────────────────────────────────────────────
function CopyButton({ value, className = '' }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e?.stopPropagation?.();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} className={`p-1.5 text-gray-400 hover:text-primary-600 transition-colors ${className}`} title="Copy">
      {copied ? <CheckCircleIcon className="h-4 w-4 text-green-500" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
    </button>
  );
}

// ── Code Snippets Modal ───────────────────────────────────────────────────────
function CodeSnippetsModal({ open, onClose, webhookUrl, apiKey, websiteName, fixedBrand }) {
  const [tab, setTab] = useState('fetch');
  if (!open) return null;

  const url = webhookUrl || 'YOUR_WEBHOOK_URL';
  const key = apiKey || 'YOUR_API_KEY';

  const snippets = {
    fetch: `fetch("${url}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    api_key: "${key}",
    name:    "John Doe",
    phone:   "9876543210",
    email:   "john@example.com",
    city:    "Mumbai",
    state:   "Maharashtra",
    brand:   "Tata",
    model:   "Nexon",
    message: "Interested in buying a car"
  })
});`,
    curl: `curl -X POST "${url}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "api_key": "${key}",
    "name":    "John Doe",
    "phone":   "9876543210",
    "email":   "john@example.com",
    "city":    "Mumbai",
    "state":   "Maharashtra",
    "brand":   "Tata",
    "model":   "Nexon",
    "message": "Interested in buying a car"
  }'`,
    php: `<?php
$data = [
  'api_key' => '${key}',
  'name'    => 'John Doe',
  'phone'   => '9876543210',
  'email'   => 'john@example.com',
  'city'    => 'Mumbai',
  'state'   => 'Maharashtra',
  'brand'   => 'Tata',
  'model'   => 'Nexon',
  'message' => 'Interested in buying a car',
];

$ch = curl_init('${url}');
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json']);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);
?>`,
  };

  const tabs = [
    { id: 'fetch', label: 'JavaScript' },
    { id: 'curl', label: 'cURL' },
    { id: 'php', label: 'PHP' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Integration Code</h3>
            <p className="text-xs text-gray-500 mt-0.5">Paste this in the contact form on <span className="font-medium">{websiteName}</span></p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 overflow-y-auto">
          <div className="flex gap-1 mb-3">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  tab === t.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="relative">
            <pre className="bg-gray-900 text-green-300 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre leading-relaxed">
              {snippets[tab]}
            </pre>
            <div className="absolute top-2 right-2">
              <CopyButton value={snippets[tab]} />
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-2">Accepted Fields</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {[
                ['api_key', 'Required'],
                ['name', 'Full name'],
                ['phone', 'Phone number'],
                ['email', 'Email address'],
                ['city', 'City'],
                ['state', 'State'],
                ['brand', 'Brand'],
                ['model', 'Model'],
                ['message', 'Enquiry message'],
              ].map(([field, desc]) => (
                <div key={field} className="text-xs">
                  <code className="font-mono text-blue-700">{field}</code>
                  <span className="text-blue-500 ml-1">— {desc}</span>
                </div>
              ))}
            </div>
            {fixedBrand && (
              <p className="text-xs text-blue-600 mt-2.5 pt-2.5 border-t border-blue-100">
                This website is set to always use <span className="font-semibold">{fixedBrand}</span> as the brand
                (Settings → Website Integration) — the <code className="font-mono">brand</code> field above is
                ignored for this site. Remove it from the snippet, or send <code className="font-mono">model</code> only.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Add / Edit Modal ──────────────────────────────────────────────────────────
function WebsiteModal({ open, onClose, source, brands, users, onSubmit, submitting }) {
  const [name, setName] = useState('');
  const [brandId, setBrandId] = useState('');
  const [routeToUserId, setRouteToUserId] = useState('');

  React.useEffect(() => {
    if (open) {
      setName(source?.name || '');
      setBrandId(source?.brandId?._id || source?.brandId || '');
      setRouteToUserId(source?.routeToUserId?._id || source?.routeToUserId || '');
    }
  }, [open, source]);

  if (!open) return null;

  const canSubmit = !!name.trim() && !!routeToUserId;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), brandId: brandId || null, routeToUserId });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">
              {source ? 'Edit Website' : 'Add Website'}
            </h3>
            <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="px-5 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Website Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Hyundai Showroom Site"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Brand</label>
              <select
                value={brandId}
                onChange={e => setBrandId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">— No brand —</option>
                {brands.map(b => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">If set, leads from this site are auto-tagged with this brand.</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Route Leads To <span className="text-red-500">*</span>
              </label>
              <select
                value={routeToUserId}
                onChange={e => setRouteToUserId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">— Select user —</option>
                {users.map(u => (
                  <option key={u._id} value={u._id}>
                    {u.firstName} {u.lastName} ({u.role?.replace('_', ' ')})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : (source ? 'Save Changes' : 'Add Website')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── API Key cell (masked / shown) ─────────────────────────────────────────────
function ApiKeyCell({ value, onRegenerate, regenerating }) {
  const [shown, setShown] = useState(false);
  const masked = value ? `${value.slice(0, 6)}••••••••${value.slice(-4)}` : '—';

  return (
    <div className="flex items-center gap-1">
      <code className="font-mono text-xs text-gray-700 select-all">{shown ? value : masked}</code>
      <button
        type="button"
        onClick={() => setShown(s => !s)}
        className="p-1 text-gray-400 hover:text-gray-600"
        title={shown ? 'Hide' : 'Show'}
      >
        {shown ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
      </button>
      <CopyButton value={value} />
      <button
        type="button"
        onClick={onRegenerate}
        disabled={regenerating}
        className="p-1 text-gray-400 hover:text-amber-600 disabled:opacity-50"
        title="Regenerate API key"
      >
        <ArrowPathIcon className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export const WebsiteIntegration = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.role === 'super_admin';

  const { data, isLoading } = useQuery('websiteSources', websiteSourceService.list, {
    enabled: isSuperAdmin,
    retry: false,
  });
  const webhookUrl = data?.data?.data?.webhookUrl || '';
  const sources = useMemo(() => data?.data?.data?.sources || [], [data]);

  const { data: usersData } = useQuery('assignableUsers', userService.getAssignableUsers, { enabled: isSuperAdmin });
  const users = usersData?.data?.data?.users || usersData?.data?.users || [];

  const { data: brandsData } = useQuery('brands', brandService.getBrands, { enabled: isSuperAdmin, staleTime: 5 * 60_000 });
  const brands = brandsData?.data?.data?.brands || [];

  const [modal, setModal] = useState({ open: false, source: null });
  const [codeModal, setCodeModal] = useState({ open: false, source: null });

  const createMutation = useMutation(websiteSourceService.create, {
    onSuccess: () => {
      toast.success('Website added');
      queryClient.invalidateQueries('websiteSources');
      setModal({ open: false, source: null });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to add'),
  });

  const updateMutation = useMutation(
    ({ id, data }) => websiteSourceService.update(id, data),
    {
      onSuccess: () => {
        toast.success('Website updated');
        queryClient.invalidateQueries('websiteSources');
        setModal({ open: false, source: null });
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to update'),
    }
  );

  const regenerateMutation = useMutation(websiteSourceService.regenerateKey, {
    onSuccess: () => {
      toast.success('API key regenerated');
      queryClient.invalidateQueries('websiteSources');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to regenerate'),
  });

  const toggleActiveMutation = useMutation(
    ({ id, isActive }) => websiteSourceService.update(id, { isActive }),
    {
      onSuccess: () => queryClient.invalidateQueries('websiteSources'),
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to update'),
    }
  );

  const deleteMutation = useMutation(websiteSourceService.remove, {
    onSuccess: () => {
      toast.success('Website deleted');
      queryClient.invalidateQueries('websiteSources');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete'),
  });

  if (!isSuperAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Website Integration</h1>
          <p className="text-sm text-gray-500 mt-1">Automatically import leads from your website contact forms</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-12 text-center">
          <p className="text-gray-500 text-sm">Contact your Super Admin to configure website integration.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = (payload) => {
    if (modal.source) {
      updateMutation.mutate({ id: modal.source._id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleRegenerate = (id) => {
    if (!window.confirm('Regenerate API key? The old key will stop working immediately — update your website code.')) return;
    regenerateMutation.mutate(id);
  };

  const handleDelete = (source) => {
    if (!window.confirm(`Delete "${source.name}"? Its API key will stop working immediately.`)) return;
    deleteMutation.mutate(source._id);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Website Integration</h1>
        <p className="text-sm text-gray-500 mt-1">Configure each website to route its leads to a specific user</p>
      </div>

      {/* Webhook URL */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <label className="block text-xs font-medium text-gray-500 mb-1">Webhook URL (same for all websites)</label>
        <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono text-gray-700 break-all">
          <span className="flex-1">{webhookUrl || '—'}</span>
          {webhookUrl && <CopyButton value={webhookUrl} />}
        </div>
        <p className="text-xs text-gray-400 mt-1">Each website uses its own API key to identify itself.</p>
      </div>

      {/* Websites table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Connected Websites</h2>
            <p className="text-sm text-gray-500 mt-0.5">{sources.length} website{sources.length === 1 ? '' : 's'}</p>
          </div>
          <button
            onClick={() => setModal({ open: true, source: null })}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add Website
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : sources.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500 text-sm">No websites connected yet.</p>
            <p className="text-gray-400 text-xs mt-1">Click "Add Website" to create your first integration.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Name</th>
                  <th className="px-5 py-2.5 text-left font-medium">Brand</th>
                  <th className="px-5 py-2.5 text-left font-medium">API Key</th>
                  <th className="px-5 py-2.5 text-left font-medium">Route To</th>
                  <th className="px-5 py-2.5 text-left font-medium">Status</th>
                  <th className="px-5 py-2.5 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sources.map(s => (
                  <tr key={s._id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-medium text-gray-900">{s.name}</td>
                    <td className="px-5 py-3 text-gray-600">{s.brandId?.name || <span className="text-gray-400">—</span>}</td>
                    <td className="px-5 py-3">
                      <ApiKeyCell
                        value={s.apiKey}
                        onRegenerate={() => handleRegenerate(s._id)}
                        regenerating={regenerateMutation.isLoading && regenerateMutation.variables === s._id}
                      />
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {s.routeToUserId
                        ? `${s.routeToUserId.firstName} ${s.routeToUserId.lastName}`
                        : <span className="text-red-500">— Missing —</span>}
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActiveMutation.mutate({ id: s._id, isActive: !s.isActive })}
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          s.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {s.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setCodeModal({ open: true, source: s })}
                          className="p-1.5 text-gray-400 hover:text-primary-600"
                          title="View integration code"
                        >
                          <CodeBracketIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setModal({ open: true, source: s })}
                          className="p-1.5 text-gray-400 hover:text-primary-600"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(s)}
                          disabled={deleteMutation.isLoading}
                          className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <WebsiteModal
        open={modal.open}
        onClose={() => setModal({ open: false, source: null })}
        source={modal.source}
        brands={brands}
        users={users}
        onSubmit={handleSubmit}
        submitting={createMutation.isLoading || updateMutation.isLoading}
      />

      <CodeSnippetsModal
        open={codeModal.open}
        onClose={() => setCodeModal({ open: false, source: null })}
        webhookUrl={webhookUrl}
        apiKey={codeModal.source?.apiKey}
        websiteName={codeModal.source?.name}
        fixedBrand={codeModal.source?.brandId?.name}
      />
    </div>
  );
};
